import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { dealsAPI, subscriptionAPI, inboxAPI, recentAnalysesAPI } from '../api';
import ReportOutcomeModal from '../components/ReportOutcomeModal';
import { outcomeChip, uiText as outcomeUiText } from '../utils/outcomeText';
import { getGuestRecentAnalyses } from '../utils/guestRecentAnalyses';
import Header from '../components/Header';
import OnboardingTooltip from '../components/OnboardingTooltip';
import { TrendingUp, BarChart3, Inbox, ArrowRight, Plus, Home, DollarSign, Calendar, MapPin, Clock, FileText, CheckCircle, AlertTriangle, Copy, Check, Share2, Users, Edit3, Save } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useContext(UserContext);
  // The saved analysis the user is reporting an outcome for: { analysisId, address, outcome }
  const [reporting, setReporting] = useState(null);
  // Outcomes reported in this session, by analysis id, so the card updates without a refetch
  const [reportedOutcomes, setReportedOutcomes] = useState({});
  const navigate = useNavigate();
  const { lang } = useTranslation();
  
  const [stats, setStats] = useState({
    savedDeals: 0,
    recentAnalyses: [], // Changed from recentDeals to recentAnalyses (auto-history)
    usage: null,
    inboxSummary: null
  });
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      const userId = user?.user_id;
      
      // Load saved deals count
      let savedDealsCount = 0;
      if (userId) {
        const dealsResponse = await dealsAPI.getSaved(userId);
        savedDealsCount = (dealsResponse.data.deals || []).length;
      }
      
      // Load recent analyses (auto-history)
      let recentAnalyses = [];
      if (userId) {
        // Authenticated user: load from server
        try {
          const recentResponse = await recentAnalysesAPI.getRecent(userId, 10);
          recentAnalyses = recentResponse.data.analyses || [];
        } catch (e) {
          console.warn('Failed to load recent analyses from server:', e);
        }
      } else {
        // Guest user: load from localStorage
        recentAnalyses = getGuestRecentAnalyses().slice(0, 10);
      }
      
      // Load usage
      let usage = null;
      if (userId) {
        try {
          const usageResponse = await subscriptionAPI.getUsageInfo(userId);
          usage = usageResponse.data;
        } catch (e) {
          console.warn('Failed to load usage info:', e);
        }
      }
      
      // Load inbox summary
      let inboxSummary = null;
      if (userId) {
        try {
          const inboxResponse = await inboxAPI.getInbox(userId);
          inboxSummary = {
            username: inboxResponse.data.username,
            submissions: inboxResponse.data.submissions || [],
            pendingCount: (inboxResponse.data.submissions || []).filter(s => s.status === 'pending').length,
            reviewedCount: (inboxResponse.data.submissions || []).filter(s => s.status === 'reviewed').length
          };
        } catch (e) {
          // Inbox not set up yet
          inboxSummary = null;
        }
      }
      
      setStats({
        savedDeals: savedDealsCount,
        recentAnalyses,
        usage,
        inboxSummary
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const getDealBadge = (rating) => {
    const configs = {
      strong: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      moderate: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: AlertTriangle },
      not_recommended: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertTriangle }
    };
    return configs[rating] || configs.moderate;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'es-ES', { 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Onboarding Tooltip for first-time users */}
      <OnboardingTooltip />

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-7xl">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-1 sm:mb-2">
            {lang === 'en' ? 'Dashboard' : 'Panel'}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground">
            {lang === 'en' 
              ? `Welcome back${user?.name ? `, ${user.name.split(' ')[0]}` : ''}!`
              : `¡Bienvenido${user?.name ? `, ${user.name.split(' ')[0]}` : ''}!`}
          </p>
        </div>

        {/* Quick Analyze Button - Prominent CTA */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/analyze')}
            className="w-full sm:w-auto bg-gradient-to-r from-secondary to-blue-700 text-white rounded-xl p-4 sm:p-6 text-left hover:shadow-lg transition-all group flex items-center gap-4"
            data-testid="quick-analyze-btn"
          >
            <div className="bg-white/20 rounded-full p-3">
              <Plus className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold mb-1">
                {lang === 'en' ? 'Analyze New Deal' : 'Analizar Nuevo Trato'}
              </h3>
              <p className="text-blue-100 text-sm">
                {lang === 'en' ? 'Paste a listing URL or enter an address' : 'Pega una URL de listado o ingresa una dirección'}
              </p>
            </div>
            <ArrowRight className="w-6 h-6 ml-auto opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
          </button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div 
            onClick={() => navigate('/saved')}
            className="bg-white border border-border rounded-xl p-4 cursor-pointer hover:shadow-md hover:border-secondary transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <Home className="w-5 h-5 text-secondary" />
              <span className="text-sm text-muted-foreground">
                {lang === 'en' ? 'Saved Deals' : 'Guardados'}
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-primary">
              {stats.savedDeals}
            </div>
          </div>

          <div 
            onClick={() => navigate('/compare')}
            className="bg-white border border-border rounded-xl p-4 cursor-pointer hover:shadow-md hover:border-secondary transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-secondary" />
              <span className="text-sm text-muted-foreground">
                {lang === 'en' ? 'Compared' : 'Comparados'}
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-primary">
              {Math.min(stats.savedDeals, 3)}/3
            </div>
          </div>

          <div 
            onClick={() => navigate('/inbox')}
            className="bg-white border border-border rounded-xl p-4 cursor-pointer hover:shadow-md hover:border-secondary transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <Inbox className="w-5 h-5 text-secondary" />
              <span className="text-sm text-muted-foreground">
                {lang === 'en' ? 'Inbox' : 'Buzón'}
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-primary">
              {stats.inboxSummary?.pendingCount || 0}
              <span className="text-sm font-normal text-muted-foreground ml-1">
                {lang === 'en' ? 'pending' : 'pendiente'}
              </span>
            </div>
          </div>

          <div className="bg-white border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-secondary" />
              <span className="text-sm text-muted-foreground">
                {lang === 'en' ? 'This Month' : 'Este Mes'}
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-primary">
              {stats.usage?.analyses_used || stats.usage?.analyses_count || 0}
              <span className="text-sm font-normal text-muted-foreground">
                /{stats.usage?.analyses_limit || 5}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          
          {/* Recent Analyses - Takes 2 columns */}
          <div className="lg:col-span-2 bg-white border border-border rounded-xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-secondary" />
                {lang === 'en' ? 'Recently Analyzed Deals' : 'Tratos Analizados Recientemente'}
              </h2>
              {stats.savedDeals > 0 && (
                <button
                  onClick={() => navigate('/saved')}
                  className="text-secondary text-sm font-medium hover:underline flex items-center gap-1"
                >
                  {lang === 'en' ? `${stats.savedDeals} Saved` : `${stats.savedDeals} Guardados`}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary mx-auto"></div>
              </div>
            ) : stats.recentAnalyses.length === 0 ? (
              <div className="text-center py-8">
                <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm mb-4">
                  {lang === 'en' ? 'No recent analyses yet' : 'Sin análisis recientes aún'}
                </p>
                <button
                  onClick={() => navigate('/analyze')}
                  className="px-4 py-2 bg-secondary text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all"
                >
                  {lang === 'en' ? 'Analyze Your First Deal' : 'Analiza Tu Primer Trato'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.recentAnalyses.map((item, index) => {
                  const data = item.analysis_data || item;
                  const badge = getDealBadge(data.deal_rating || item.deal_rating);
                  const BadgeIcon = badge.icon;
                  const isSaved = item.is_saved;
                  // A valuation isn't a "deal" being rated — hide the score/rating pill.
                  const isValuation = (data.strategy || item.strategy || 'flip').toLowerCase() === 'valuation';
                  const cardAnalysisId = data.analysis_id || data.id || item.analysis_id;
                  // What the user already reported, if anything: this session first, then the server
                  const outcome = (cardAnalysisId && reportedOutcomes[cardAnalysisId]) || item.outcome || null;
                  const chip = outcomeChip(outcome, lang);

                  return (
                    <div key={item.recent_id || index} className="flex flex-col sm:flex-row sm:items-stretch gap-2">
                    <div
                      onClick={() => {
                        const analysisId = data.analysis_id || data.id || item.analysis_id;
                        if (analysisId) {
                          navigate(`/results/${analysisId}`, { state: { analysis: data } });
                        } else {
                          navigate('/results/view', { state: { analysis: data } });
                        }
                      }}
                      className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-lg border border-border hover:border-secondary hover:bg-blue-50 cursor-pointer transition-all gap-3"
                      data-testid={`recent-analysis-${index}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin className="w-4 h-4 text-secondary flex-shrink-0" />
                          <h3 className="font-medium truncate text-sm sm:text-base">
                            {item.address || data.address}
                          </h3>
                          {/* Draft/Saved Badge */}
                          {isSaved ? (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              <Save className="w-3 h-3" />
                              {lang === 'en' ? 'Saved' : 'Guardado'}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              <Edit3 className="w-3 h-3" />
                              {lang === 'en' ? 'Draft' : 'Borrador'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs sm:text-sm text-muted-foreground">
                          <span>{item.city || data.city}, {item.state || data.state}</span>
                          {(item.sqft || data.sqft) && <span>{(item.sqft || data.sqft)?.toLocaleString()} sqft</span>}
                          {(item.beds || data.beds) && <span>{item.beds || data.beds} bed</span>}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 sm:gap-6">
                        {/* Strategy-aware metric — no phantom flip Net Profit on valuation/rental rows */}
                        {isValuation ? (
                          <div className="text-left sm:text-right">
                            {(() => {
                              const arv = data.arv || 0;
                              const equity = arv - (data.purchase_price || 0);
                              return (
                                <>
                                  <div className="text-base sm:text-lg font-bold text-primary">${arv.toLocaleString()}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {lang === 'en' ? 'Est. Value' : 'Valor Est.'} · <span className={equity >= 0 ? 'text-success' : 'text-danger'}>${equity.toLocaleString()} {lang === 'en' ? 'equity' : 'capital'}</span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        ) : (data.strategy || item.strategy || 'flip').toLowerCase() === 'rental' ? (
                          <div className="text-left sm:text-right">
                            <div className={`text-base sm:text-lg font-bold ${(data.rental_analysis?.monthly_cashflow || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                              ${(data.rental_analysis?.monthly_cashflow || 0).toLocaleString()}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {lang === 'en' ? 'Cash flow/mo' : 'Flujo/mes'}
                            </div>
                          </div>
                        ) : (
                          <div className="text-left sm:text-right">
                            <div className={`text-base sm:text-lg font-bold ${(item.net_profit || data.flip_analysis?.net_profit) >= 0 ? 'text-success' : 'text-danger'}`}>
                              ${(item.net_profit || data.flip_analysis?.net_profit)?.toLocaleString()}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {lang === 'en' ? 'Net profit' : 'Ganancia neta'}
                            </div>
                          </div>
                        )}
                        
                        {!isValuation && (
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                            <BadgeIcon className="w-3 h-3" />
                            {Math.round(item.deal_score || data.deal_score || 0)}/100
                          </div>
                        )}
                      </div>
                    </div>

                    {/* HOW THE DEAL ENDED — one compact button beside the card, outside its
                        click. Blue while the report is still to be made; green "✓ Reported"
                        once it is. Pressing it again opens the same flow to correct the
                        report. The card navigates; the button never does. What was reported
                        ("Vendido · a 2.4%") is visible text beside the green button — not a
                        tooltip, which a phone cannot show. */}
                    {cardAnalysisId && (
                      <div className="flex items-center gap-2 sm:flex-col sm:items-end sm:gap-1 sm:self-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setReporting({ analysisId: cardAnalysisId, address: item.address || data.address, outcome })}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold text-white whitespace-nowrap transition-colors ${
                            outcome ? 'bg-green-600 hover:bg-green-700' : 'bg-secondary hover:bg-blue-700'
                          }`}
                          data-testid="report-outcome"
                          data-state={outcome ? 'reported' : 'pending'}
                        >
                          {outcome ? outcomeUiText(lang).reported : outcomeUiText(lang).report}
                        </button>
                        {chip && (
                          <span className="text-xs font-medium text-green-700 whitespace-nowrap" data-testid="outcome-chip">
                            {chip}
                          </span>
                        )}
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Deal Inbox Summary */}
            <div className="bg-white border border-border rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold mb-4 flex items-center gap-2">
                <Inbox className="w-5 h-5 text-secondary" />
                {lang === 'en' ? 'Deal Inbox' : 'Buzón de Tratos'}
              </h2>
              
              {stats.inboxSummary?.username ? (
                <div>
                  {/* Submission Link - Prominent */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 text-secondary font-semibold mb-2">
                      <Share2 className="w-4 h-4" />
                      {lang === 'en' ? 'Your Submission Link' : 'Tu Enlace de Envío'}
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1 bg-white border border-border rounded-lg px-3 py-2 font-mono text-sm truncate">
                        chekste.com/submit/{stats.inboxSummary.username}
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`https://chekste.com/submit/${stats.inboxSummary.username}`);
                          setCopiedLink(true);
                          toast.success(lang === 'en' ? 'Link copied!' : '¡Enlace copiado!');
                          setTimeout(() => setCopiedLink(false), 2000);
                        }}
                        className={`p-2 rounded-lg transition-all ${
                          copiedLink 
                            ? 'bg-green-500 text-white' 
                            : 'bg-secondary text-white hover:bg-blue-700'
                        }`}
                        data-testid="copy-inbox-link"
                      >
                        {copiedLink ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-start gap-2">
                      <Users className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {lang === 'en' 
                        ? 'Share this link with wholesalers and agents so they can submit deals directly to your inbox for review.'
                        : 'Comparte este enlace con mayoristas y agentes para que puedan enviar tratos directamente a tu buzón para revisión.'}
                    </p>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <div className="text-2xl font-bold text-amber-600">
                        {stats.inboxSummary.pendingCount}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {lang === 'en' ? 'Pending Review' : 'Pendientes'}
                      </div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {stats.inboxSummary.reviewedCount}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {lang === 'en' ? 'Reviewed' : 'Revisados'}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => navigate('/inbox')}
                    className="w-full px-4 py-2 bg-secondary text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                  >
                    {lang === 'en' ? 'View Inbox' : 'Ver Buzón'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Inbox className="w-8 h-8 text-secondary" />
                  </div>
                  <h3 className="font-semibold text-primary mb-2">
                    {lang === 'en' ? 'Set Up Your Deal Inbox' : 'Configura Tu Buzón de Tratos'}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {lang === 'en' 
                      ? 'Create a unique link to receive deal submissions from wholesalers and agents.'
                      : 'Crea un enlace único para recibir envíos de tratos de mayoristas y agentes.'}
                  </p>
                  <button
                    onClick={() => navigate('/inbox')}
                    className="px-6 py-2 bg-secondary text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all"
                  >
                    {lang === 'en' ? 'Set Up Inbox' : 'Configurar Buzón'}
                  </button>
                </div>
              )}
            </div>

            {/* Usage Card */}
            {stats.usage && (
              <div className="bg-white border border-border rounded-xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold mb-4">
                  {stats.usage.is_code_active 
                    ? (lang === 'en' ? 'Complimentary Usage' : 'Uso de Cortesía')
                    : stats.usage.period === 'lifetime'
                    ? (lang === 'en' ? 'Lifetime Usage' : 'Uso de Por Vida')
                    : (lang === 'en' ? 'Monthly Usage' : 'Uso Mensual')}
                </h2>
                <div className="text-center">
                  <div className="text-4xl sm:text-5xl font-bold text-primary mb-2">
                    {stats.usage.analyses_used || stats.usage.analyses_count || 0}
                  </div>
                  <div className="text-muted-foreground text-sm mb-4">
                    {lang === 'en' ? 'of' : 'de'} {stats.usage.analyses_limit || 5} {lang === 'en' ? 'analyses' : 'análisis'}
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
                    <div 
                      className="bg-secondary rounded-full h-2 transition-all"
                      style={{ width: `${Math.min(((stats.usage.analyses_used || stats.usage.analyses_count || 0) / (stats.usage.analyses_limit || 5)) * 100, 100)}%` }}
                    ></div>
                  </div>
                  {stats.usage.is_code_active && (
                    <p className="text-amber-600 text-xs font-medium mb-2">
                      {lang === 'en' ? '🎁 Access code active' : '🎁 Código de acceso activo'}
                    </p>
                  )}
                  {user?.plan_id === 'free' && !stats.usage.is_code_active && (
                    <button
                      onClick={() => navigate('/billing')}
                      className="text-secondary text-sm font-medium hover:underline"
                    >
                      {lang === 'en' ? 'Upgrade your plan' : 'Mejora tu plan'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {reporting && (
        <ReportOutcomeModal
          analysisId={reporting.analysisId}
          address={reporting.address}
          existing={reporting.outcome}
          lang={lang}
          onClose={() => setReporting(null)}
          onSaved={(outcome) => setReportedOutcomes((prev) => ({ ...prev, [reporting.analysisId]: outcome }))}
        />
      )}
    </div>
  );
};

export default Dashboard;
